import React, { useEffect, useState } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  MenuItem,
  Box,
  IconButton,
  Autocomplete,
  CircularProgress,
  Stack,
  Typography,
  InputAdornment,
} from "@mui/material";
import { Close } from "@mui/icons-material";
import ButtonOne from "../../common/normal-button";
import ButtonTwo from "../../common/dashboard-button";
import { useAuth } from "../../hooks/AuthProvider";
import { MFD_CLIENTS_ALL_ENDPOINT } from "../../config/urls";
import typography from "../../cssUtils/typography";
import { ColorsConstants } from "../../cssUtils/constants";

const STATUS_OPTIONS = ["Open", "In Progress", "Converted", "Not Interested"];

const EMPTY_ERRORS = { client_id: "", goal: "" };

const EMPTY_FORM = {
  client_id:     null,
  goal:          "",
  target_amount: "",
  timeline:      "",
  status:        "Open",
  comment:       "",
};

// Strip null/undefined to keep TextField as a controlled "" input
const safe = (v) => (v == null ? "" : v);

const GoalPlanFormDialog = ({ open, handleClose, onSave, editData, saving = false }) => {
  const { getValidToken } = useAuth();

  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState(EMPTY_ERRORS);
  const [clientOptions, setClientOptions]   = useState([]);
  const [clientsLoading, setClientsLoading] = useState(false);

  // Fetch clients for autocomplete
  useEffect(() => {
    if (!open) return;
    setClientsLoading(true);
    (async () => {
      try {
        const token  = await getValidToken();
        const tenant = localStorage.getItem("tenant");
        const res = await fetch(MFD_CLIENTS_ALL_ENDPOINT, {
          headers: { Authorization: `Bearer ${token}`, "X-Tenant-Id": tenant },
        });
        if (!res.ok) throw new Error("Failed to fetch clients");
        setClientOptions(await res.json());
      } catch (err) {
        console.error("Error fetching clients:", err);
      } finally {
        setClientsLoading(false);
      }
    })();
  }, [open, getValidToken]);

  // Hydrate form on open / edit. `editData` is initially the list row;
  // the parent then fetches detail and re-passes — so we re-hydrate when
  // any of the relevant fields change.
  useEffect(() => {
    if (editData) {
      setForm({
        client_id:     editData.client ?? editData.client_id ?? null,
        goal:          safe(editData.goal),
        target_amount: safe(editData.target_amount),
        timeline:      safe(editData.timeline),
        status:        editData.status || "Open",
        comment:       safe(editData.comment),
      });
    } else {
      setForm(EMPTY_FORM);
    }
    setErrors(EMPTY_ERRORS);
  }, [editData, open]);

  const set = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
    if (field in EMPTY_ERRORS) setErrors((prev) => ({ ...prev, [field]: "" }));
  };

  const selectedClient = clientOptions.find((c) => c.id === form.client_id) ?? null;

  const handleClientChange = (_, newValue) => {
    setForm((prev) => ({ ...prev, client_id: newValue?.id ?? null }));
    setErrors((prev) => ({ ...prev, client_id: "" }));
  };

  const validate = () => {
    const next = { ...EMPTY_ERRORS };
    if (!form.client_id) next.client_id = "Client (PAN) is required";
    if (!form.goal.trim()) next.goal = "Goal is required";
    setErrors(next);
    return Object.values(next).every((e) => !e);
  };

  const handleSave = () => {
    if (!validate()) return;
    onSave(form);
  };

  return (
    <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
      <DialogTitle sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
        {editData ? "Edit Goal Plan" : "Add Goal Plan"}
        <IconButton onClick={handleClose} disabled={saving}>
          <Close />
        </IconButton>
      </DialogTitle>

      <DialogContent>
        <Box display="flex" flexDirection="column" gap={2} mt={1}>
          {/* Client selection */}
          <Autocomplete
            options={clientOptions}
            loading={clientsLoading}
            value={selectedClient}
            onChange={handleClientChange}
            isOptionEqualToValue={(opt, val) => opt.id === val.id}
            getOptionLabel={(opt) => opt.pan || ""}
            renderInput={(params) => (
              <TextField
                {...params}
                label="Client (PAN)"
                required
                error={!!errors.client_id}
                helperText={errors.client_id}
                InputProps={{
                  ...params.InputProps,
                  endAdornment: (
                    <>
                      {clientsLoading ? <CircularProgress size={18} /> : null}
                      {params.InputProps.endAdornment}
                    </>
                  ),
                }}
              />
            )}
            renderOption={(props, option) => (
              <Stack
                {...props}
                key={option.id}
                direction="row"
                gap={0.5}
                sx={{
                  alignItems: "flex-start !important",
                  textAlign: "left",
                  width: "100%",
                  justifyContent: "space-between !important",
                }}
              >
                <Typography sx={{ ...typography.bodyLarge }}>{option.pan}</Typography>
                <Typography sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray40 }}>
                  {option.source}
                </Typography>
              </Stack>
            )}
          />

          <TextField
            label="Goal"
            value={form.goal}
            onChange={(e) => set("goal", e.target.value)}
            placeholder="e.g. Retirement, Child Education"
            fullWidth
            required
            error={!!errors.goal}
            helperText={errors.goal}
          />

          <Box display="flex" gap={2}>
            <TextField
              label="Target Amount"
              type="number"
              value={form.target_amount}
              onChange={(e) => set("target_amount", e.target.value)}
              fullWidth
              InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
            />
            <TextField
              label="Timeline"
              type="number"
              value={form.timeline}
              onChange={(e) => set("timeline", e.target.value)}
              fullWidth
              InputProps={{ endAdornment: <InputAdornment position="end">years</InputAdornment> }}
            />
          </Box>

          <TextField
            select
            label="Status"
            value={form.status}
            onChange={(e) => set("status", e.target.value)}
            fullWidth
          >
            {STATUS_OPTIONS.map((opt) => (
              <MenuItem key={opt} value={opt}>{opt}</MenuItem>
            ))}
          </TextField>

          <TextField
            label="Comment"
            value={form.comment}
            onChange={(e) => set("comment", e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </Box>
      </DialogContent>

      <DialogActions sx={{ p: 2, px: 3 }}>
        <ButtonTwo onClick={handleClose} disabled={saving}>Cancel</ButtonTwo>
        <ButtonOne onClick={handleSave} disabled={saving}>
          {saving ? "Saving…" : editData ? "Update" : "Add"}
        </ButtonOne>
      </DialogActions>
    </Dialog>
  );
};

export default GoalPlanFormDialog;
